import React, {PropsWithChildren} from 'react';
import styled, {createGlobalStyle} from 'styled-components';
import {Transition, TransitionProps} from '../Transition';
import {getTimeouts} from '../utils';

type HeightTransitionProps = Pick<
  TransitionProps,
  'mounted' | 'duration' | 'delay' | 'preventAnimationOnMount'
> & {
  className?: string;
};

export const HeightTransition: React.FC<
  PropsWithChildren<HeightTransitionProps>
> = props => {
  const {children, mounted, duration, delay, preventAnimationOnMount} = props;
  const nodeRef = React.useRef(null);

  const timeouts = getTimeouts({duration, delay});
  const animationDuration = mounted
    ? timeouts.mountDuration
    : timeouts.unmountDuration;
  const animationDelay = mounted ? timeouts.mountDelay : timeouts.unmountDelay;

  const shouldAnimate =
    preventAnimationOnMount && mounted ? 'pause' : 'running';

  const styles = {
    animation: `${
      mounted ? 'heightTransitionEnter' : 'heightTransitionExit'
    } ${animationDuration}ms ease-in-out ${animationDelay}ms forwards ${shouldAnimate}`,
  };

  return (
    <Transition
      nodeRef={nodeRef}
      mounted={mounted}
      duration={duration}
      delay={delay}
    >
      <Wrapper className={props.className} style={styles}>
        <Container>
          {React.Children.map(children, (child: any) => {
            return React.cloneElement(child, {
              style: {
                ...(child.props?.style ? child.props.style : {}),
                ...styles,
              },
              ref: nodeRef,
            });
          })}
          <Styles />
        </Container>
      </Wrapper>
    </Transition>
  );
};

const Wrapper = styled.div`
  display: grid;
`;

const Container = styled.div`
  overflow: hidden;
`;

const Styles = createGlobalStyle`
  @keyframes heightTransitionEnter {
    from {
      grid-template-rows: 0fr;
    }
    to {
      grid-template-rows: 1fr;
    }
  }
  @keyframes heightTransitionExit {
    from {
      grid-template-rows: 1fr;
    }
    to {
      grid-template-rows: 0fr;
    }
  }
`;
